import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/app/types/store';
import { selectMenuItems } from '@/entities/layout';

import { STATIC_MENU_ITEMS } from './constants';
import type { NavigationItem, NavigationItemWithState } from './types';

/**
 * Combines static frontend menu items with dynamic backend items
 * Static items always come first
 */
export const selectNavigationItems = createSelector([selectMenuItems], (backendItems): NavigationItem[] => {
  return [...STATIC_MENU_ITEMS, ...backendItems];
});

export const selectNavigationState = (state: RootState) => state.navigation;

export const selectExpandedItems = createSelector([selectNavigationState], (navigation) => navigation.expandedItems);

export const selectIsCollapsed = createSelector([selectNavigationState], (navigation) => navigation.isCollapsed);

export const selectNavigationItemsWithState = createSelector(
  [selectNavigationItems, selectNavigationState, (_, currentPath: string) => currentPath],
  (items, navigationState, currentPath): NavigationItemWithState[] => {
    const enrichItem = (item: NavigationItem): NavigationItemWithState => {
      const subItems = item.subItems?.map(enrichItem);
      const isActive = item.path === currentPath || subItems?.some((sub) => sub.isActive) || false;

      return {
        ...item,
        isActive,
        isExpanded: !!navigationState.expandedItems[item.code],
        subItems
      };
    };

    return items.map(enrichItem);
  }
);
